'use client'

import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { stegaClean } from 'next-sanity'

import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { loadScriptOnce } from '@/app/utils/loadScript'
import { cn } from '@/app/utils/style'
import type { SocialEmbed as SocialEmbedType } from '@/sanity.types'
import { parseSocialUrl, type SocialEmbedTarget } from '@/utils/studio/embedPatterns'

const TWITTER_SCRIPT = 'https://platform.twitter.com/widgets.js'
const INSTAGRAM_SCRIPT = 'https://www.instagram.com/embed.js'
const RENDER_TIMEOUT_MS = 10000
// Embeds render well before they scroll into view, so their height change happens off-screen
const LOAD_AHEAD_MARGIN = '1500px'
// Space held while an embed loads; real heights vary (X posts range from about 400 to 1300px), so this
// only limits the jump and the long lookahead hides the rest
const RESERVED_HEIGHT = { twitter: 450, instagram: 600, tiktok: 740 } as const

declare global {
  interface Window {
    twttr?: {
      widgets: {
        createTweet: (
          tweetId: string,
          container: HTMLElement,
          options?: { dnt?: boolean },
        ) => Promise<HTMLElement | undefined>
      }
    }
    instgrm?: { Embeds: { process: (container?: HTMLElement) => void } }
  }
}

function renderTweet(target: SocialEmbedTarget, container: HTMLElement): Promise<boolean> {
  return loadScriptOnce(TWITTER_SCRIPT).then(async () => {
    const rendered = await window.twttr?.widgets.createTweet(target.postId, container, {
      dnt: true,
    })
    return rendered !== undefined
  })
}

function renderInstagram(target: SocialEmbedTarget, container: HTMLElement): Promise<boolean> {
  const quote = document.createElement('blockquote')
  quote.className = 'instagram-media'
  quote.setAttribute('data-instgrm-permalink', target.canonicalUrl)
  quote.setAttribute('data-instgrm-version', '14')
  container.appendChild(quote)
  return loadScriptOnce(INSTAGRAM_SCRIPT).then(
    () =>
      new Promise<boolean>((resolve) => {
        const timeout = window.setTimeout(() => {
          observer.disconnect()
          resolve(false)
        }, RENDER_TIMEOUT_MS)
        const check = () => {
          if (!container.querySelector('iframe')) return
          window.clearTimeout(timeout)
          observer.disconnect()
          resolve(true)
        }
        const observer = new MutationObserver(check)
        observer.observe(container, { childList: true, subtree: true })
        window.instgrm?.Embeds.process(container)
        // The script may already have replaced the blockquote while it loaded
        check()
      }),
  )
}

// TikTok's player iframe needs no loader script; its content sets the height
const TIKTOK_PLAYER_HEIGHT = 740

function renderTikTok(target: SocialEmbedTarget, container: HTMLElement): Promise<boolean> {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe')
    frame.src = `https://www.tiktok.com/embed/v2/${target.postId}`
    frame.title = 'TikTok video'
    frame.loading = 'lazy'
    frame.allow = 'fullscreen'
    frame.className = 'w-full border-0'
    frame.style.height = `${TIKTOK_PLAYER_HEIGHT}px`
    frame.addEventListener('load', () => resolve(true), { once: true })
    window.setTimeout(() => resolve(false), RENDER_TIMEOUT_MS)
    container.appendChild(frame)
  })
}

const RENDERERS = {
  twitter: renderTweet,
  instagram: renderInstagram,
  tiktok: renderTikTok,
} as const

const LABELS = {
  twitter: 'View this post on X',
  instagram: 'View this post on Instagram',
  tiktok: 'View this video on TikTok',
} as const

interface SocialEmbedProps {
  value: SocialEmbedType
  className?: string
}

export function SocialEmbed({ value, className }: SocialEmbedProps) {
  const widgetRef = useRef<HTMLDivElement>(null)
  const [wrapperRef, isNear] = useInView({ rootMargin: LOAD_AHEAD_MARGIN, triggerOnce: true })
  const [status, setStatus] = useState<'loading' | 'rendered' | 'failed'>('loading')
  const target = parseSocialUrl(stegaClean(value.url))
  const platform = target?.platform
  const postId = target?.postId

  useEffect(() => {
    const container = widgetRef.current
    if (!isNear || !target || !container) return
    let cancelled = false
    RENDERERS[target.platform](target, container)
      .then((rendered) => {
        if (!cancelled) setStatus(rendered ? 'rendered' : 'failed')
      })
      .catch(() => {
        if (!cancelled) setStatus('failed')
      })
    return () => {
      cancelled = true
      container.replaceChildren()
    }
    // target is derived from platform and postId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNear, platform, postId])

  if (!target) return null

  const label = LABELS[target.platform]

  return (
    <div
      ref={wrapperRef}
      className={cn(ARTICLE_BLOCK_SPACING, 'flex flex-col items-center', className)}
    >
      <div
        ref={widgetRef}
        style={status === 'loading' ? { minHeight: RESERVED_HEIGHT[target.platform] } : undefined}
        className="flex w-full max-w-[550px] flex-col items-center [&_iframe]:!mx-auto"
      />
      {status !== 'rendered' && (
        <a
          href={target.canonicalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="size-body-sm underline"
        >
          {label}
        </a>
      )}
    </div>
  )
}
